import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useAccess } from '../auth/AccessContext.jsx';
import { ACTIVITY_EVENTS } from '../auth/idle.js';
import { useLiveTopic } from '../live/LiveContext.js';
import { BusinessDayContext } from './BusinessDayContext.js';
import { createDayPoller } from './dayPoller.js';

/**
 * The one read of the business day (IP-134; design 2026-10-06 section 8.1):
 * GET /books/days (@lfoc/domain/business-day/reads.js), shared by the topbar
 * chip, its drawer and the approval screens through BusinessDayContext, so
 * the Console asks once however many places show the day.
 *
 *   day           the whole answer as last read - { now, today,
 *                 openDealingDay, reviewPaused, stages, days } - or null
 *                 before the first one
 *   reviewPaused  the answer's reviewPaused when a step pauses reviews
 *                 (IP-129) - { paused: true, businessDate, stage, stageLabel,
 *                 since } - else null
 *   refresh()     read again now: every order action calls it when it
 *                 finishes, and the drawer when it opens
 *   error         the last read's failure ({ message, retryInMs }), null once
 *                 a read succeeds; the last answer is kept meanwhile
 *
 * When it reads is dayPoller.js's: at once, then once a heartbeat while the
 * person is active - the authorizer counts every call as activity, so the
 * poll never keeps an idle session alive - asleep while the page is hidden or
 * the idle warning shows, backing off on errors to five minutes. It listens
 * for the same activity the idle monitor does (auth/idle.js ACTIVITY_EVENTS).
 *
 * Mounted inside the AccessProvider, around the Workspace (Console.jsx). A
 * person whose claims open no businessDay screen (only core.access.admin, or
 * the fee claims: GET /books/days would refuse them) is never polled for it,
 * and every consumer sees the context's default.
 */
const BusinessDayProvider = ({ children }) => {
    const { api, canView } = useAccess();
    const enabled = canView('businessDay');
    const [state, setState] = useState({ view: null, error: null });
    const pollerRef = useRef(null);

    useEffect(() => {
        if (!enabled) return undefined;
        const poller = createDayPoller({
            load: () => api.books.days(),
            onAnswer: (view) => setState({ view, error: null }),
            onError: (error, { retryInMs }) => setState((s) => ({ ...s, error: { message: error.message, retryInMs } })),
            isHidden: () => document.hidden === true
        });
        pollerRef.current = poller;
        const onActivity = () => poller.activity();
        const onVisibility = () => poller.visibility();
        ACTIVITY_EVENTS.forEach((name) => window.addEventListener(name, onActivity, { passive: true }));
        document.addEventListener('visibilitychange', onVisibility);
        poller.start();
        return () => {
            poller.stop();
            pollerRef.current = null;
            ACTIVITY_EVENTS.forEach((name) => window.removeEventListener(name, onActivity));
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, [api, enabled]);

    const refresh = useCallback(() => pollerRef.current?.refresh(), []);
    // Live screens (docs/live-sync/implementation-plan.md 4.6): the day moved on
    // somewhere - a close run, a price, a statement, someone's DAY_ADVANCE -
    // read it now instead of at the poll's next minute. The poll stays as the
    // safety net for a hint the socket missed.
    useLiveTopic(['businessDay'], refresh, { enabled });
    const value = useMemo(() => ({
        day: state.view,
        reviewPaused: state.view?.reviewPaused?.paused ? state.view.reviewPaused : null,
        refresh,
        error: state.error
    }), [state, refresh]);

    return <BusinessDayContext.Provider value={value}>{children}</BusinessDayContext.Provider>;
};

export default BusinessDayProvider;
